import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class BrowserLanguageService {
	// Focus on DE, EN, and FR as specified
	private readonly supportedLangs = ['de', 'en', 'fr'];
	private readonly defaultLang = 'de';
	private readonly storageKey = 'selectedLanguage'; // Match existing localStorage key

	constructor(
		private translate: TranslateService
	) {}

	/**
	 * Called once at app bootstrap via APP_INITIALIZER
	 */
	async init(): Promise<void> {
		// Register languages with TranslateService
		this.translate.addLangs(this.supportedLangs);
		this.translate.setDefaultLang(this.defaultLang);

		// 1) Respect manually chosen language (existing pattern)
		const savedLang = localStorage.getItem(this.storageKey);
		if (savedLang && this.supportedLangs.includes(savedLang)) {
			this.translate.use(savedLang);
			return Promise.resolve();
		}

		// 2) Detect language from browser settings
		const browserLang = this.normalizeBrowserLang(navigator.language);
		const selectedLang = this.supportedLangs.includes(browserLang)
			? browserLang
			: this.defaultLang;

		this.translate.use(selectedLang);
		localStorage.setItem(this.storageKey, selectedLang);
	}

	/**
	 * Normalize browser language to our format
	 */
	private normalizeBrowserLang(lang: string | undefined): string {
		if (!lang) {
			return this.defaultLang;
		}
		// "de-DE" -> "de", "fr-FR" -> "fr", "en-US" -> "en"
		return lang.split('-')[0].toLowerCase();
	}

	/**
	 * Helper method for manual language changes
	 * Integrates with existing SelectLanguagePopoverComponent pattern
	 */
	changeLanguage(lang: string): void {
		if (this.supportedLangs.includes(lang)) {
			this.translate.use(lang);
			localStorage.setItem(this.storageKey, lang);
		}
	}

	/**
	 * Get current language
	 */
	getCurrentLanguage(): string {
		return this.translate.currentLang || this.defaultLang;
	}

	/**
	 * Check if language was manually set vs auto-detected
	 */
	isLanguageManuallySet(): boolean {
		return !!localStorage.getItem(this.storageKey);
	}

	/**
	 * Reset to automatic detection (browser language)
	 */
	resetToAutomatic(): Promise<void> {
		localStorage.removeItem(this.storageKey);
		return this.init();
	}
}